import { z } from "zod";

export type JobCategoryType = z.infer<typeof JobCategorySchema>;
export type JobType = z.infer<typeof JobSchema>;
export type BusinessCategoryType = z.infer<typeof BusinessCategorySchema>;
export type BusinessType = z.infer<typeof BusinessSchema>;
export type GenderType = z.infer<typeof GenderSchema>;
export type UserRoleType = z.infer<typeof UserRoleTypeSchema>;
export type NotificationDeliverySetting = z.infer<typeof NotificationDeliverySettingSchema>;
export type NotificationGroup = z.infer<typeof NotificationGroupSchema>;
export type UserType = z.infer<typeof UserSchema>;
export type UserProfileType = z.infer<typeof UserProfileSchema>;
export type UserProfileCreateType = z.infer<typeof UserProfileCreateSchema>;
export type SchoolType = z.infer<typeof SchoolSchema>;
export type AcademicCategoryType = z.infer<typeof AcademicCategorySchema>;

export const JobCategorySchema = z.enum([
  "strategic_comprehensive_consultant",
  "system_it_consultant",
  "trader_dealer",
  "corporate_planning_business_planning",
  "research_marketing",
  "product_planning_planning",
  "advertising_public_relations",
  "general_affairs_hr_labor_relations",
  "accounting_finance_accounting",
  "legal",
  "director_producer",
  "web_engineer",
  "data_scientist",
  "system_engineer",
  "smartphone_app_engineer",
  "machine_learning_engineer",
  "basic_research_applied_research",
  "mechanical_electrical_electronic_design",
  "general_sales",
  "technical_sales_system_sales",
  "game_creator",
  "ui_ux_designer",
  "editing_production",
  "comprehensive_position",
  "public_officer",
  "pharmacist"
]);

export const JobSchema = z.object({
  id: z.number(),
  name: JobCategorySchema
});

export const jobLabelMap: Record<JobCategoryType, string> = {
  strategic_comprehensive_consultant: "戦略・総合コンサルタント",
  system_it_consultant: "システム・ITコンサルタント",
  trader_dealer: "トレーダー・ディーラー",
  corporate_planning_business_planning: "経営企画・事業企画",
  research_marketing: "調査・マーケティング",
  product_planning_planning: "商品企画・プランニング",
  advertising_public_relations: "宣伝・広報",
  general_affairs_hr_labor_relations: "総務・人事・労務",
  accounting_finance_accounting: "経理・財務・会計",
  legal: "法務",
  director_producer: "ディレクター・プロデューサー",
  web_engineer: "webエンジニア",
  data_scientist: "データサイエンティスト",
  system_engineer: "SE",
  smartphone_app_engineer: "スマホアプリエンジニア",
  machine_learning_engineer: "機械学習エンジニア",
  basic_research_applied_research: "基礎研究・応用研究",
  mechanical_electrical_electronic_design: "機械・電気・電子機器設計",
  general_sales: "一般営業",
  technical_sales_system_sales: "技術営業・システム営業",
  game_creator: "ゲームクリエイター",
  ui_ux_designer: "UI・UXデザイナー",
  editing_production: "編集・制作",
  comprehensive_position: "総合職",
  public_officer: "公務員",
  pharmacist: "薬剤師"
};

export const BusinessCategorySchema = z.enum([
  "it_communications_internet",
  "manufacturer",
  "trading_company",
  "service_leisure",
  "distribution_retail_food",
  "mass_media_advertising_design",
  "finance_insurance",
  "consulting",
  "real_estate_construction_facilities",
  "environment_energy",
  "public_institution"
]);

export const BusinessSchema = z.object({
  id: z.number(),
  name: BusinessCategorySchema
});

export const businessLabelMap: Record<BusinessCategoryType, string> = {
  it_communications_internet: "IT・通信・インターネット",
  manufacturer: "メーカー",
  trading_company: "商社",
  service_leisure: "サービス・レジャー",
  distribution_retail_food: "流通・小売・フード",
  mass_media_advertising_design: "マスコミ・広告・デザイン",
  finance_insurance: "金融・保険",
  consulting: "コンサルティング",
  real_estate_construction_facilities: "不動産・建築・設備",
  environment_energy: "環境・エネルギー",
  public_institution: "公的機関"
};

export const GenderSchema = z.enum(["NA", "male", "female", "other"]);

export const genderLabelMap: Readonly<Record<GenderType, string>> = {
  NA: "未選択",
  male: "男性",
  female: "女性",
  other: "未解答"
};

export const NotificationDeliverySettingSchema = z
  .object({
    id: z.number(),
    notification_group_id: z.number(),
    user_id: z.number(),
    status: z.string(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),

    // INFO: なぜかidとかが大文字になっててバリデーションできません
    // user: UserResponseSchema,

    notification_group: z.lazy(() => NotificationGroupSchema).optional()
  })
  .passthrough();

export const NotificationGroupSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  notification_delivery_settings: NotificationDeliverySettingSchema.array().nullable()
});

export type UpsertMySettingParams = {
  unsubscribe_group_ids: string;
  subscribe_group_ids: string;
};

export type PrefectureType = z.infer<typeof PrefectureSchema>;

export const PrefectureSchema = z.enum([
  "NA",
  "Hokkaido",
  "Aomori",
  "Iwate",
  "Miyagi",
  "Akita",
  "Yamagata",
  "Fukushima",
  "Ibaraki",
  "Tochigi",
  "Gunma",
  "Saitama",
  "Chiba",
  "Tokyo",
  "Kanagawa",
  "Niigata",
  "Toyama",
  "Ishikawa",
  "Fukui",
  "Yamanashi",
  "Nagano",
  "Gifu",
  "Shizuoka",
  "Aichi",
  "Mie",
  "Shiga",
  "Kyoto",
  "Osaka",
  "Hyogo",
  "Nara",
  "Wakayama",
  "Tottori",
  "Shimane",
  "Okayama",
  "Hiroshima",
  "Yamaguchi",
  "Tokushima",
  "Kagawa",
  "Ehime",
  "Kochi",
  "Fukuoka",
  "Saga",
  "Nagasaki",
  "Kumamoto",
  "Oita",
  "Miyazaki",
  "Kagoshima",
  "Okinawa"
]);

export const prefectureLabelMap: Readonly<Record<PrefectureType, string>> = {
  NA: "未選択",
  Hokkaido: "北海道",
  Aomori: "青森県",
  Iwate: "岩手県",
  Miyagi: "宮城県",
  Akita: "秋田県",
  Yamagata: "山形県",
  Fukushima: "福島県",
  Ibaraki: "茨城県",
  Tochigi: "栃木県",
  Gunma: "群馬県",
  Saitama: "埼玉県",
  Chiba: "千葉県",
  Tokyo: "東京都",
  Kanagawa: "神奈川県",
  Niigata: "新潟県",
  Toyama: "富山県",
  Ishikawa: "石川県",
  Fukui: "福井県",
  Yamanashi: "山梨県",
  Nagano: "長野県",
  Gifu: "岐阜県",
  Shizuoka: "静岡県",
  Aichi: "愛知県",
  Mie: "三重県",
  Shiga: "滋賀県",
  Kyoto: "京都府",
  Osaka: "大阪府",
  Hyogo: "兵庫県",
  Nara: "奈良県",
  Wakayama: "和歌山県",
  Tottori: "鳥取県",
  Shimane: "島根県",
  Okayama: "岡山県",
  Hiroshima: "広島県",
  Yamaguchi: "山口県",
  Tokushima: "徳島県",
  Kagawa: "香川県",
  Ehime: "愛媛県",
  Kochi: "高知県",
  Fukuoka: "福岡県",
  Saga: "佐賀県",
  Nagasaki: "長崎県",
  Kumamoto: "熊本県",
  Oita: "大分県",
  Miyazaki: "宮崎県",
  Kagoshima: "鹿児島県",
  Okinawa: "沖縄県"
};

export const SchoolSchema = z.enum([
  "college",
  "graduate_school",
  "college_of_technology",
  "junior_college",
  "high_school",
  "vacational_school"
]);

export const schoolLabelMap: Readonly<Record<SchoolType, string>> = {
  college: "大学",
  graduate_school: "大学院",
  college_of_technology: "高専",
  junior_college: "短大",
  high_school: "高等学校",
  vacational_school: "専門学校"
};

export const AcademicCategorySchema = z.enum([
  "NA",
  "information_technology",
  "electrical_engineering",
  "mechanical_engineering",
  "mathematics",
  "architectural_engineering",
  "biochemistry",
  "earth_sciences",
  "economics",
  "law",
  "education",
  "human_sciences",
  "physical_education",
  "human_environmental_sciences",
  "international_studies",
  "literature",
  "art"
]);

export const academicCategoryLabelMap: Readonly<Record<AcademicCategoryType, string>> = {
  NA: "未選択",
  information_technology: "情報工学",
  electrical_engineering: "電気工学",
  mechanical_engineering: "機械工学",
  mathematics: "数学",
  architectural_engineering: "建築学",
  biochemistry: "生化学",
  earth_sciences: "地球科学",
  economics: "経済学",
  law: "法学",
  education: "教育学",
  human_sciences: "人間科学",
  physical_education: "体育学",
  human_environmental_sciences: "人間環境学",
  international_studies: "国際学",
  literature: "文学",
  art: "芸術学"
};

export const UserProfileSchema = z
  .object({
    id: z.number()
  })
  .merge(
    z
      .object({
        sex: GenderSchema,
        school_type: SchoolSchema,
        school_name: z.string(),
        academic_category: AcademicCategorySchema,
        major: z.string().nullable(),
        graduate_year: z.number(),
        phone_number: z.string().nullable(),
        postal_code: z.string().nullable(),
        prefecture: PrefectureSchema,
        city: z.string().nullable(),
        address1: z.string().nullable(),
        address2: z.string().nullable(),
        self_pr: z.string().nullable(),
        interesting_jobs: JobSchema.array().nullable(),
        interesting_businesses: BusinessSchema.array().nullable()
      })
      .partial()
  );

export const UserProfileCreateSchema = z
  .object({
    sex: z.string(),
    school_type: SchoolSchema,
    school_name: z.string(),
    academic_category: AcademicCategorySchema,
    major: z.string(),
    graduate_year: z.number(),
    phone_number: z.string(),
    postal_code: z.string(),
    prefecture: PrefectureSchema,
    city: z.string(),
    address1: z.string(),
    address2: z.string(),
    self_pr: z.string(),
    interesting_jobs: JobSchema.array(),
    interesting_businesses: BusinessSchema.array()
  })
  .partial();

export const UserRoleTypeSchema = z.enum(["guest", "free", "pro", "admin"]);

export const UserSchema = z.object({
  id: z.number(),
  email: z.string().email().nullable(),
  uuid: z.string().uuid(),
  name: z.string().nullable(),
  role: UserRoleTypeSchema,
  icon: z.string().url().nullable(),
  available_credit: z.number().int(),
  purchased: z.boolean(),
  expiry_at: z.string().nullable()
});
